/**
 * Audio settings panel.
 *
 * Renders four user-controlled audio knobs:
 *
 *  - **Microphone device** — populated from
 *    `navigator.mediaDevices.enumerateDevices()` filtered to
 *    `audioinput`. Choice takes effect on the *next* PTT press —
 *    swapping mid-press would whip the OS through a route change
 *    while the user is mid-sentence.
 *
 *  - **Microphone gain** — a 0.5×–8× multiplier applied per-sample
 *    to the captured int16 frame on the main thread, before RMS
 *    calculation and before Opus encode. Default 1×. We disable the
 *    browser's `autoGainControl` (see `capture.ts`) which means
 *    Android phones with a weak/distant mic underdrive the encoder.
 *    A fixed user-tunable multiplier solves that without re-introducing
 *    Chrome's pumpy AGC. The VU meter reflects post-gain level so the
 *    user can dial it in against what the encoder will actually see.
 *
 *  - **Speaker device** — populated from `audiooutput`. Honoured via
 *    `AudioContext.setSinkId` on browsers that support it (recent
 *    Chromium). On Safari/iOS the dropdown is replaced with a short
 *    note pointing the user at the OS audio-route picker, since the
 *    web platform offers no programmatic route control there.
 *
 *  - **Speaker volume** — a 0–200% multiplier applied via a
 *    `GainNode` between the playback worklet and `ctx.destination`.
 *    Default 100%. Lets a user counteract a phone whose system
 *    volume keeps being trimmed by OS-level "loud-sound protection"
 *    or Bluetooth volume sync — they crank the in-app gain past
 *    unity, the OS keeps the system level low, and the per-stream
 *    boost compensates. Boost past unity will eventually clip into
 *    the playback worklet's soft-clipper.
 *
 * Selections are persisted to `localStorage` so a paired AirPods, a
 * preferred USB headset, and a per-device gain calibration are all
 * remembered across reloads.
 *
 * **Device labels are private.** The browser only fills in real
 * device names after at least one `getUserMedia` permission has been
 * granted on this origin. Until then the dropdowns may show a
 * generic "audioinput" / "audiooutput" entry only — that is a
 * platform limitation, not a bug. After the user makes their first
 * PTT press (or after `precacheMicPermission()` resolves) the labels
 * populate.
 */

const STORAGE_KEY = "nettacco.audio.settings";

/** Mic gain slider range. */
const MIC_GAIN_MIN = 0.5;
const MIC_GAIN_MAX = 8.0;
const MIC_GAIN_DEFAULT = 1.0;

/** Speaker volume slider range. 2.0 is the maximum boost we offer. */
const SPEAKER_VOLUME_MIN = 0.0;
const SPEAKER_VOLUME_MAX = 2.0;
const SPEAKER_VOLUME_DEFAULT = 1.0;

/** Persisted user audio settings. All fields default to "system / unity". */
export interface AudioSettings {
    /** Microphone deviceId, or undefined for system default. */
    micDeviceId?: string;
    /** Speaker deviceId, or undefined for system default. */
    speakerDeviceId?: string;
    /**
     * Linear multiplier on the captured int16 samples. 1.0 is unity;
     * values >1 amplify, values <1 attenuate. Stored unclamped but
     * the UI restricts to [`MIC_GAIN_MIN`, `MIC_GAIN_MAX`].
     */
    micGain?: number;
    /**
     * Linear multiplier on the playback graph. 1.0 is unity; >1 boosts
     * past system volume (with eventual soft-clipping); 0 mutes.
     */
    speakerVolume?: number;
}

/**
 * Clamp `n` into `[min, max]` after coercing to a finite number.
 * Out-of-range or non-finite inputs return `fallback`.
 */
function clampNumber(n: unknown, min: number, max: number, fallback: number): number {
    if (typeof n !== "number" || !Number.isFinite(n)) return fallback;
    if (n < min) return min;
    if (n > max) return max;
    return n;
}

/**
 * Read the user's stored audio settings. Returns an empty object on
 * first run, after a clear, or if the storage entry is malformed.
 * Numeric fields are clamped on read so a hand-edited or out-of-range
 * value can never break the UI.
 */
export function loadAudioSettings(): AudioSettings {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw === null) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (typeof parsed !== "object" || parsed === null) return {};
        const obj = parsed as Record<string, unknown>;
        const sel: AudioSettings = {};
        if (typeof obj["micDeviceId"] === "string") {
            sel.micDeviceId = obj["micDeviceId"];
        }
        if (typeof obj["speakerDeviceId"] === "string") {
            sel.speakerDeviceId = obj["speakerDeviceId"];
        }
        if (obj["micGain"] !== undefined) {
            sel.micGain = clampNumber(
                obj["micGain"],
                MIC_GAIN_MIN,
                MIC_GAIN_MAX,
                MIC_GAIN_DEFAULT,
            );
        }
        if (obj["speakerVolume"] !== undefined) {
            sel.speakerVolume = clampNumber(
                obj["speakerVolume"],
                SPEAKER_VOLUME_MIN,
                SPEAKER_VOLUME_MAX,
                SPEAKER_VOLUME_DEFAULT,
            );
        }
        return sel;
    } catch {
        return {};
    }
}

function saveAudioSettings(sel: AudioSettings): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
    } catch {
        // localStorage may be unavailable in private mode; the panel
        // continues to work for the rest of the session.
    }
}

/** Constructor options for [`createAudioSettings`]. */
export interface AudioSettingsOptions {
    /** Container the panel is appended into. */
    container: HTMLElement;
    /** Initial selection — typically from [`loadAudioSettings`]. */
    initial: AudioSettings;
    /**
     * Fired whenever the user changes any control. The argument is
     * the **new full selection** so callers don't need to merge.
     * Persistence is handled internally — callers don't need to save.
     * Slider drags fire on every input tick; downstream consumers
     * should compare against the previous value if their work is
     * non-trivial.
     */
    onChange: (selection: AudioSettings) => void;
}

/** Handle returned by [`createAudioSettings`] for refresh and teardown. */
export interface AudioSettingsHandle {
    /**
     * Re-query `enumerateDevices()` and repopulate the dropdowns.
     * Useful after the first `getUserMedia` grant — that's when device
     * labels become readable on most browsers.
     */
    refresh(): Promise<void>;
    /** Detach the panel and unsubscribe from device-change events. */
    destroy(): void;
}

const speakerSelectionSupported = (): boolean =>
    typeof AudioContext !== "undefined" &&
    "setSinkId" in AudioContext.prototype;

/**
 * Render the audio-settings panel into `container`. The container
 * controls visibility (typically toggled by a settings button in the
 * channel header); the panel itself always renders its full body.
 */
export function createAudioSettings(opts: AudioSettingsOptions): AudioSettingsHandle {
    let current: AudioSettings = { ...opts.initial };

    const root = document.createElement("div");
    root.className = "audio-settings";

    const buildField = (text: string, child: HTMLElement): HTMLElement => {
        const lbl = document.createElement("label");
        const span = document.createElement("span");
        span.textContent = text;
        lbl.appendChild(span);
        lbl.appendChild(child);
        return lbl;
    };

    // ---- microphone device --------------------------------------------
    const micSelect = document.createElement("select");
    micSelect.className = "audio-settings-mic";
    root.appendChild(buildField("Microphone", micSelect));

    // ---- microphone gain ----------------------------------------------
    const micGainInitial = current.micGain ?? MIC_GAIN_DEFAULT;
    const micGain = buildSlider({
        label: "Microphone gain",
        min: MIC_GAIN_MIN,
        max: MIC_GAIN_MAX,
        step: 0.1,
        initial: micGainInitial,
        format: (v) => `${v.toFixed(1)}×`,
    });
    root.appendChild(micGain.root);

    // ---- speaker device -----------------------------------------------
    const supportsSpeaker = speakerSelectionSupported();
    const speakerSelect = document.createElement("select");
    speakerSelect.className = "audio-settings-speaker";

    if (supportsSpeaker) {
        root.appendChild(buildField("Speaker", speakerSelect));
    } else {
        const note = document.createElement("p");
        note.className = "audio-settings-note";
        note.textContent =
            "This browser doesn't support choosing the speaker output. " +
            "Use your OS audio-route picker (Control Center on iOS, " +
            "system sound settings elsewhere) to switch between phone, " +
            "loudspeaker, and Bluetooth.";
        root.appendChild(note);
    }

    // ---- speaker volume -----------------------------------------------
    const speakerVolumeInitial = current.speakerVolume ?? SPEAKER_VOLUME_DEFAULT;
    const speakerVolume = buildSlider({
        label: "Speaker volume",
        min: SPEAKER_VOLUME_MIN,
        max: SPEAKER_VOLUME_MAX,
        step: 0.05,
        initial: speakerVolumeInitial,
        format: (v) => `${Math.round(v * 100)}%`,
    });
    root.appendChild(speakerVolume.root);

    const populate = (
        select: HTMLSelectElement,
        devices: MediaDeviceInfo[],
        currentId: string | undefined,
    ): void => {
        select.replaceChildren();
        const def = document.createElement("option");
        def.value = "";
        def.textContent = "System default";
        select.appendChild(def);
        for (const d of devices) {
            const opt = document.createElement("option");
            opt.value = d.deviceId;
            opt.textContent = d.label !== "" ? d.label : `(unnamed ${d.kind})`;
            select.appendChild(opt);
        }
        // If the previously saved deviceId is no longer present, fall
        // back to default so the select isn't blank.
        const wanted = currentId ?? "";
        const found = Array.from(select.options).some((o) => o.value === wanted);
        select.value = found ? wanted : "";
    };

    const refresh = async (): Promise<void> => {
        let devices: MediaDeviceInfo[] = [];
        try {
            devices = await navigator.mediaDevices.enumerateDevices();
        } catch {
            return;
        }
        populate(micSelect, devices.filter((d) => d.kind === "audioinput"), current.micDeviceId);
        if (supportsSpeaker) {
            populate(
                speakerSelect,
                devices.filter((d) => d.kind === "audiooutput"),
                current.speakerDeviceId,
            );
        }
    };

    const emit = (): void => {
        saveAudioSettings(current);
        opts.onChange(current);
    };

    const onMicChange = (): void => {
        const v = micSelect.value;
        current = { ...current, micDeviceId: v === "" ? undefined : v };
        emit();
    };
    const onSpeakerChange = (): void => {
        const v = speakerSelect.value;
        current = { ...current, speakerDeviceId: v === "" ? undefined : v };
        emit();
    };
    const onMicGainChange = (v: number): void => {
        current = { ...current, micGain: v };
        emit();
    };
    const onSpeakerVolumeChange = (v: number): void => {
        current = { ...current, speakerVolume: v };
        emit();
    };

    micSelect.addEventListener("change", onMicChange);
    speakerSelect.addEventListener("change", onSpeakerChange);
    micGain.input.addEventListener("input", () => onMicGainChange(parseFloat(micGain.input.value)));
    speakerVolume.input.addEventListener("input", () =>
        onSpeakerVolumeChange(parseFloat(speakerVolume.input.value)),
    );

    // Devices come and go (Bluetooth pair/unpair, USB plug, AirPods
    // case open/close). Keep the dropdowns live.
    const onDeviceChange = (): void => {
        void refresh();
    };
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);

    opts.container.appendChild(root);
    void refresh();

    return {
        async refresh(): Promise<void> {
            await refresh();
        },
        destroy(): void {
            micSelect.removeEventListener("change", onMicChange);
            speakerSelect.removeEventListener("change", onSpeakerChange);
            navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
            root.remove();
        },
    };
}

/** Slider construction options. */
interface SliderSpec {
    label: string;
    min: number;
    max: number;
    step: number;
    initial: number;
    /** How to render the numeric value next to the label (e.g. "1.5×", "75%"). */
    format: (value: number) => string;
}

/** A range input with a label-row that shows the live numeric value. */
interface SliderControl {
    /** The wrapper element ready to append into the panel. */
    root: HTMLElement;
    /** The underlying `<input type="range">` for event wiring. */
    input: HTMLInputElement;
}

/**
 * Build a labelled range slider with a live numeric readout. The
 * caller wires the `input.input` event for value changes — we do the
 * formatting/value sync internally so the readout stays accurate
 * regardless of whether the change came from drag, keyboard, or
 * programmatic assignment.
 */
function buildSlider(spec: SliderSpec): SliderControl {
    const wrap = document.createElement("label");
    wrap.className = "audio-settings-slider";

    const row = document.createElement("span");
    row.className = "audio-settings-slider-row";

    const text = document.createElement("span");
    text.textContent = spec.label;

    const value = document.createElement("span");
    value.className = "audio-settings-slider-value";
    value.textContent = spec.format(spec.initial);

    row.appendChild(text);
    row.appendChild(value);

    const input = document.createElement("input");
    input.type = "range";
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.value = String(spec.initial);

    // Keep the readout in lockstep with the slider regardless of who
    // changed the value. `input` fires for drag, keyboard, and direct
    // value assignment dispatching; `change` fires only on commit.
    input.addEventListener("input", () => {
        const v = parseFloat(input.value);
        if (Number.isFinite(v)) value.textContent = spec.format(v);
    });

    wrap.appendChild(row);
    wrap.appendChild(input);

    return { root: wrap, input };
}
